import React, { FunctionComponent, useMemo } from 'react';
import { useStore } from 'effector-react';
import mentionsOfficialAccountComment, {
  getList,
  PAGE_SIZE_STORAGE_KEY,
} from '@stores/mentionsOfficialAccountComment';
import { Spin, Empty, Button, Select } from 'antd';
import { getMentionsPageSize, mentionsPageSizeOptions } from '@constants/pagination';
import MentionsOfficialAccountCommentCard from '@components/MentionsOfficialAccountCommentCard';
import componentContent from './MentionsOfficialAccountCommentList.content.json';
import useTranslate from '@hooks/useTranslate';
import css from './MentionsOfficialAccountCommentList.module.less';

interface MentionsOfficialAccountCommentListProps {}

const MentionsOfficialAccountCommentList: FunctionComponent<
  MentionsOfficialAccountCommentListProps
> = () => {
  const { list, hasMore, nextLastDate, preloaders, pagination, setPagination } = useStore(
    mentionsOfficialAccountComment,
  );
  const content = useTranslate(componentContent);

  const isListLoading = useMemo(() => !!preloaders.getList, [preloaders]);
  const currentPageSize = getMentionsPageSize(
    localStorage.getItem(PAGE_SIZE_STORAGE_KEY) || pagination.pageSize,
  );

  const pageSizeChange = (pageSize: number) => {
    localStorage.setItem(PAGE_SIZE_STORAGE_KEY, `${pageSize}`);
    setPagination({ ...pagination, pageSize });
    getList({ pagination: { pageSize, lastDate: null } });
  };

  const loadMore = () => {
    getList({ pagination: { pageSize: currentPageSize, lastDate: nextLastDate } });
  };

  if (!list.length && !isListLoading && !hasMore) {
    return <Empty />;
  }

  return (
    <div>
      <div className={css.controls}>
        <Select
          value={currentPageSize}
          onChange={pageSizeChange}
          disabled={isListLoading}
        >
          {mentionsPageSizeOptions.map((pageSize) => (
            <Select.Option
              value={Number(pageSize)}
              key={pageSize}
            >
              {pageSize}
            </Select.Option>
          ))}
        </Select>
      </div>

      <Spin spinning={isListLoading}>
        <div className={css.root}>
          {list.length ? (
            list.map((comment) => (
              <MentionsOfficialAccountCommentCard
                key={comment.id}
                comment={comment}
              />
            ))
          ) : (
            <Empty />
          )}
        </div>
      </Spin>

      {hasMore && (
        <div className={css.controls}>
          <Button
            onClick={loadMore}
            loading={isListLoading}
          >
            {content.show_more}
          </Button>
        </div>
      )}
    </div>
  );
};

export default MentionsOfficialAccountCommentList;
